import { useId, VStack } from '@chakra-ui/react-v2';
import { Field, Form, Formik } from 'formik';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { lazy, object, string } from 'yup';

import { useBulkUpdateQuantities } from '@frontend/api';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  DrawerBody,
  DrawerButtonGroup,
  DrawerFooter,
  FormControl,
  FormLabel,
  Popover,
  PopoverTrigger,
  Portal,
  Radio,
  RadioGroup,
} from '@frontend/legacy-components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import Quantities from 'modules/quotes/quoteItems/components/Quantities';
import { refreshQuoteItem } from 'modules/quotes/quoteItems/data/actions';
import {
  getExpandedQuoteItemId,
  getQuoteItemsByIdsDenormalized,
} from 'modules/quotes/quoteItems/data/selectors';
import { MAX_QUANTITY_VALUE } from 'modules/quotes/quoteItems/modals/Quantities/const';
import type { QuantityData } from 'modules/quotes/quoteItems/modals/Quantities/types';
import { setQuantityAtIndex } from 'modules/quotes/quoteItems/modals/Quantities/utils';

import { refreshQuote } from 'store/quotes/actions';
import { getQuoteId } from 'store/quotes/selectors';
import { useFeatureFlagValue } from 'store/session/hooks';

import { notifyError, notifySuccess } from 'utils/notifyUtils';
import { pluralize } from 'utils/stringUtils';

import { quoteItemSelect } from '../data/actions';
import { ConfirmAddQuantitiesPopoverContent } from './ConfirmAddQuantitiesPopoverContent';
import { ConfirmReplaceQuantitiesPopoverContent } from './ConfirmReplaceQuantitiesPopoverContent';
import * as actions from './data/actions';
import { getQuoteItemIds } from './data/reducer';
import type { FormValues, QuoteItemData } from './types';

const MESSAGES = {
  action: 'To update quantities, select an action',
};

const INITIAL_VALUES: FormValues = {
  action: 'add',
};

export const BulkEditQuantitiesDrawerForm = () => {
  const submitButtonRef = useRef(null);
  const quoteId = useSelector(getQuoteId);
  const displayedQuoteItemId = useSelector(getExpandedQuoteItemId);
  const quoteItemIds = useSelector(getQuoteItemIds);
  const isAdvancedNestingEnabled = useFeatureFlagValue(
    featureFlagKeys.isAdvancedNestingEnabled,
  );
  const isMostLikelyWonQuantityEnabled = useFeatureFlagValue(
    'isMostLikelyWonQuantityEnabled',
  );
  const maximumQuoteItemQuantities = useFeatureFlagValue(
    'maxQuoteItemQuantities',
  );
  const [displayedQuantities, setDisplayedQuantities] = useState<
    QuantityData[]
  >(Array.from({ length: maximumQuoteItemQuantities }, () => ({})));

  const quoteItems: QuoteItemData[] = useSelector((state) =>
    getQuoteItemsByIdsDenormalized(state, quoteItemIds),
  );

  const hasSingleComponentNesting = useMemo(() => {
    return quoteItems.some(
      ({ hasSingleComponentNest }) => hasSingleComponentNest,
    );
  }, [quoteItems]);
  const formId = useId(undefined, 'bulk-edit-quantities-drawer-form');

  const onChangeHandler = (index: number, quantity: QuantityData) => {
    const newQuantities = setQuantityAtIndex(
      displayedQuantities,
      index,
      quantity,
    );

    setDisplayedQuantities(newQuantities);
  };

  const hasError = useMemo(
    () =>
      displayedQuantities.some(
        ({ quantity }) => quantity != null && quantity > MAX_QUANTITY_VALUE,
      ),
    [displayedQuantities],
  );

  const dispatch = useDispatch();
  const onClose = () => dispatch(actions.close());

  const { mutate: bulkUpdateQuantities, isLoading } = useBulkUpdateQuantities({
    queryOptions: {
      onSuccess: (_response, requestData) => {
        dispatch(refreshQuote.request());
        if (displayedQuoteItemId) {
          dispatch(
            refreshQuoteItem({
              quoteItemId: displayedQuoteItemId,
              shouldRecalculate: false,
            }),
          );
        }
        dispatch(quoteItemSelect.deselectAll());
        const successMessage = `Quantities ${
          requestData.shouldReplaceExistingQuantities
            ? 'replaced on'
            : 'added to'
        } ${pluralize('line item', requestData.quantityData.length)}`;
        notifySuccess(successMessage);
        onClose();
      },
      onError: (error, requestData) => {
        const failureMessage = `Failed to ${
          requestData.shouldReplaceExistingQuantities ? 'replace' : 'add'
        } quantities`;
        notifyError(failureMessage, (error as Error)?.message);
      },
    },
  });

  const onSubmit = useCallback(
    (values: FormValues) => {
      const quantities = displayedQuantities
        .filter(({ quantity }) => quantity != null)
        .map(({ quantity }) => quantity)
        .join(',');
      const mostLikelyWonQuantityData = displayedQuantities.find(
        ({ isMostLikelyWonQuantity }) => isMostLikelyWonQuantity,
      );
      let mostLikelyWonQuantity: number | undefined = undefined;
      let mostLikelyWonQuantityPercent: number | null | undefined = undefined;
      if (mostLikelyWonQuantityData) {
        mostLikelyWonQuantity = mostLikelyWonQuantityData.quantity;
        mostLikelyWonQuantityPercent =
          mostLikelyWonQuantityData.mostLikelyWonQuantityPercent;
      }
      const quantityData = quoteItemIds.map((quoteItemId) => ({
        quoteItemId,
        quantities,
        mostLikelyWonQuantity,
        mostLikelyWonQuantityPercent,
      }));
      bulkUpdateQuantities({
        quoteId,
        quantityData,
        shouldReplaceExistingQuantities: values.action === 'replace',
      });
    },
    [bulkUpdateQuantities, quoteId, quoteItemIds, displayedQuantities],
  );

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={lazy(() =>
        object().shape({
          action: string().required(MESSAGES.action),
        }),
      )}
    >
      {({ setFieldValue, values }) => {
        return (
          <>
            <DrawerBody>
              <Form id={formId} noValidate autoComplete="off">
                <VStack
                  spacing={3}
                  alignItems={
                    isMostLikelyWonQuantityEnabled ? 'center' : 'flex-start'
                  }
                >
                  <FormControl isRequired>
                    <FormLabel>Action</FormLabel>
                    <Field
                      name="action"
                      as={RadioGroup}
                      onChange={(v: string) => setFieldValue('action', v)}
                    >
                      <Radio value="add">Add quantities</Radio>
                      <Radio value="replace">Replace all quantities</Radio>
                    </Field>
                  </FormControl>
                  <Quantities
                    isReadOnly={isLoading}
                    isMostLikelyWonQuantityEnabled={
                      isMostLikelyWonQuantityEnabled
                    }
                    quantities={displayedQuantities}
                    initialQuantities={displayedQuantities}
                    onChange={onChangeHandler}
                  />
                  <Alert overflow="visible">
                    <AlertIcon />
                    <AlertDescription>
                      Overrides will have to be applied for quantity-specific
                      costing variables on newly added quantities.
                    </AlertDescription>
                  </Alert>
                  {isAdvancedNestingEnabled && hasSingleComponentNesting && (
                    <Alert overflow="visible">
                      <AlertIcon />
                      <AlertDescription>
                        New nests will have to be computed on newly added
                        quantities.
                      </AlertDescription>
                    </Alert>
                  )}
                </VStack>
              </Form>
            </DrawerBody>
            <DrawerFooter>
              <DrawerButtonGroup>
                <Button
                  variant="secondary"
                  isLoading={isLoading}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Popover
                  placement="top-end"
                  size="md"
                  initialFocusRef={submitButtonRef}
                >
                  {({ onClose: onClosePopover }) => (
                    <>
                      <PopoverTrigger>
                        <Button
                          type="submit"
                          form={formId}
                          isLoading={isLoading}
                          isDisabled={hasError}
                        >
                          Save Changes
                        </Button>
                      </PopoverTrigger>
                      <Portal>
                        {values.action === 'replace' ? (
                          <ConfirmReplaceQuantitiesPopoverContent
                            onCancel={() => onClosePopover()}
                            onConfirm={() => {
                              onClosePopover();
                              onSubmit(values);
                            }}
                            numberOfLineItems={quoteItemIds.length}
                            submitButtonRef={submitButtonRef}
                          />
                        ) : (
                          <ConfirmAddQuantitiesPopoverContent
                            onCancel={() => onClosePopover()}
                            onConfirm={() => {
                              onClosePopover();
                              onSubmit(values);
                            }}
                            submitButtonRef={submitButtonRef}
                          />
                        )}
                      </Portal>
                    </>
                  )}
                </Popover>
              </DrawerButtonGroup>
            </DrawerFooter>
          </>
        );
      }}
    </Formik>
  );
};
